import * as React from 'react';
import {Button, Input, Modal, Tab, Tabs} from 'react-bootstrap';
import Formsy from 'formsy-react';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Promise from 'bluebird';

import {SubmitButton, SubscribeFormFields} from './forms';
import * as Locale from '../model/locale';
import {TEXT_7DAY_DATA, TEXT_LIVE_DATA, TEXT_TODAY_DATA, TEXT_YESTERDAY_DATA} from '../model/locale';
import * as Browser from "../../src/model/core/browser";
import {UserData} from '../model/account';

import Select from 'react-select';
import * as _ from 'underscore';
import {ExportReportTypes} from "../model/reports";

interface ExportModalProps extends React.Props<ExportModalProps> {
    textForKey: Function;
    exportAction: Function;
    getUnitsAction: Function;
    user: UserData;
    state: {
        show: boolean;
        onHide: Function;
    };
    reportType?: any;
    premisesUnits?: any;
    sensorsLoading?: boolean;
}

export class ExportModal extends React.Component<ExportModalProps, any> {
    initialState: {
        startDate: any;
        endDate: any;
        reportType: any;
        downloadDisabled?: boolean;
        selectedSensors?: any;
        sensorChoices?: any;
        selectedInterval: any;
    };
    state: {
        startDate: any;
        endDate: any;
        url?: string;
        exporter?: Promise<any>;
        error?: string;
        reportType?: any;
        downloadDisabled?: boolean;
        selectedSensors?: any;
        sensorChoices?: any;
        selectedInterval: any;
    };

    constructor() {
        super();
        this.initialState = {
            startDate: moment().subtract(6, 'days'),
            endDate: moment(),
            reportType: ExportReportTypes.DATA,
            downloadDisabled: false,
            selectedSensors: [],
            sensorChoices: [],
            selectedInterval: '0'
        };
        this.state = this.initialState;
    }

    onHide() {
        let {exporter} = this.state;
        this.props.state.onHide();
        if (exporter) {
            exporter.cancel();
        }
        this.state = this.initialState;
    }

    onExport() {
        let {textForKey, exportAction} = this.props;
        let {startDate, endDate, reportType} = this.state;
        let sensorMap = this.getExportSensorMap();
        let interval = this.getInterval();
        let _this = this;

        if (interval && reportType === ExportReportTypes.DATA) {
            reportType = ExportReportTypes.HACCP_INTERVAL;
        }

        let exportPromise = exportAction(
            startDate, endDate, reportType, sensorMap, interval
        );
        exportPromise.then((response) => {
            if (response.url) {
                Browser.setWindowLocation(response.url);
            }
            _this.setState({
                url: response.url,
                error: response.error,
                exporter: null,
                selectedSensors: []
            });
        });
        exportPromise.catch(() => {
            _this.setState({
                error: textForKey(Locale.TEXT_MODAL_EXPORT_ERROR),
                exporter: null,
                selectedSensors: [],
            });
        });

        let newState = {
            exporter: exportPromise
        };
        this.setState(newState);
    }

    onDateChange(newStartDate, newEndDate) {
        this.setState({
            startDate: newStartDate,
            endDate: newEndDate
        });
    }

    onIntervalChange(selection) {
        this.setState({
            selectedInterval: selection.value
        });
    }

    onReportTypeSelect(selection) {
        let sensorsRequired = ExportReportTypes.areSensorsRequired(selection.value);
        this.setState({
            reportType: selection.value,
            downloadDisabled: sensorsRequired && this.state.selectedSensors.length === 0
        });
        if (selection.value === ExportReportTypes.DATA_DUMP) {
            // data dump report defaults to 6 hour interval
            this.setState({
                selectedInterval: '' + (6 * 3600)
            });
        }
        if (sensorsRequired) {
            this.loadCabinetsData();
        }
    }

    loadCabinetsData() {
        let {getUnitsAction} = this.props;

        if (!this.props.sensorsLoading && this.state.sensorChoices.length === 0) {
            if (typeof getUnitsAction === "function") {
                let getUnitsActionPromise = getUnitsAction();
                getUnitsActionPromise.then(() => {
                    this.setState({downloadDisabled: true});
                    this.updateSensorChoices();
                }).catch(() => {
                    console.log('getUnitsActionPromise error');
                });
            } else {
                this.updateSensorChoices();
            }
        }
    }

    onSensorSelect(values) {
        if (values.length <= 12) {
            this.setState({
                selectedSensors: values,
                downloadDisabled: values.length === 0
            });
        }
    }

    getExportSensorMap() {
        let sensorMap = {};
        this.state.selectedSensors.forEach(function(selection) {
            let unitSensor = selection.value.split('|');
            if (!(unitSensor[0] in sensorMap)) {
                sensorMap[unitSensor[0]] = [];
            }
            sensorMap[unitSensor[0]].push(unitSensor[1]);
        });
        return sensorMap;
    }

    getInterval() {
        let interval = parseInt(this.state.selectedInterval, 10);
        return interval === 0 ? null : interval;
    }

    updateSensorChoices() {
        let {textForKey} = this.props;
        let unitsSensors = [];
        this.props.premisesUnits.forEach(function (cabinet) {
            cabinet.sensors.forEach(function (sensor) {
                let label = textForKey(Locale.TEXT_MODAL_EXPORT_SENSORS_SEPARATOR,
                                       {unit: cabinet.description, sensor: sensor.sensor_description});
                unitsSensors.push(
                    {
                        label: label,
                        value: cabinet.unit_id + '|' + sensor.key
                    });
            });
        });
        this.setState({
            sensorChoices: unitsSensors,
        });
    }

    render() {
        let {textForKey, state, user} = this.props;
        let {startDate, endDate} = this.state;

        // set display properties of dialog based on current state
        let showDownload = true;
        let downloadProps: any = {};
        let cancelText = textForKey(Locale.TEXT_MODAL_BUTTON_CANCEL);
        let downloadText = textForKey(Locale.TEXT_MODAL_BUTTON_DOWNLOAD);
        let body = null;
        let reportOptions = [
            {
                label: textForKey(Locale.TEXT_MODAL_EXPORT_REPORT_TYPE_DATA),
                value: ExportReportTypes.DATA
            },
            {
                label: textForKey(Locale.TEXT_MODAL_EXPORT_REPORT_TYPE_HACCP),
                value: ExportReportTypes.HACCP
            }];
        if (user.has_admin_access) {
            reportOptions.push({
                label: textForKey(Locale.TEXT_MODAL_EXPORT_REPORT_TYPE_DATA_DUMP),
                value: ExportReportTypes.DATA_DUMP
            });
        }

        let reportSpecificContent = null;
        if (ExportReportTypes.areSensorsRequired(this.state.reportType)) {
            reportSpecificContent = <Select
                name="form-field-name"
                value={this.state.selectedSensors}
                options={this.state.sensorChoices}
                className="haccp-sensors"
                placeholder={textForKey(Locale.TEXT_MODAL_EXPORT_SENSOR_PLACEHOLDER)}
                noResultsText={textForKey(Locale.TEXT_MODAL_EXPORT_SENSOR_NO_RESULTS)}
                multi={true}
                onChange={this.onSensorSelect.bind(this)}
            />;
        } else {
            const localeData = moment.localeData();
            let hourLabel = (h) => localeData.relativeTime(h, false, 'hh', false);
            let hourOpt = (h) => ({label: hourLabel(h), value: String(h * 3600)});
            const opts = [
                {label: textForKey(Locale.TEXT_INTERVAL_NONE), value: '0'},
                /// moment doesn't provide a 1 hour locale value, only "an hour"
                // hh format string is plural, so we need to translate manually
                {label: textForKey(Locale.TEXT_INTERVAL_ONE_HOUR), value: '3600'},
                hourOpt(2), hourOpt(3), hourOpt(4), hourOpt(5), hourOpt(6)
            ];
            const intervalSelect = <Select
                name="report-interval"
                value={this.state.selectedInterval}
                options={opts}
                placeholder={textForKey(Locale.TEXT_MODAL_EXPORT_DATA_INTERVAL_LABEL)}
                onChange={this.onIntervalChange.bind(this)}
            />;

            reportSpecificContent = <div>
                <p>{textForKey(Locale.TEXT_MODAL_EXPORT_DATA_INTERVAL_LABEL)}</p>
            {intervalSelect}
            </div>;
        }

        if (this.state.exporter) {
            // in the process of downloading
            downloadProps.disabled = true;
            body = (
                <div className="progress-bar"></div>
            );
        } else if (this.state.error) {
            // failed to download
            downloadText = textForKey(Locale.TEXT_MODAL_BUTTON_RETRY);
            body = (
                <p>{this.state.error}</p>
            );
        } else if (this.state.url) {
            // successfully downloaded
            cancelText = textForKey(Locale.TEXT_MODAL_BUTTON_CLOSE);
            showDownload = false;
            // must embed unescaped to get anchor link in text displaying
            let markup = {
                __html: textForKey(Locale.TEXT_MODAL_EXPORT_FILE_GENERATED,
                                   {url: this.state.url})
            };
            body = (
                <div dangerouslySetInnerHTML={markup}></div>
            );
        } else {
            let extraOptions: any = {};
            if (user.has_admin_access &&
                    ExportReportTypes.isDump(this.state.reportType)) {
                extraOptions.dateLimit = {years: 5};
            }
            // allow selection of options before export
            body = (
                <div>
                    <Select
                        name="text"
                        options={reportOptions}
                        addLabelText="label"
                        clearable= {false}
                        placeholder={textForKey(Locale.TEXT_MODAL_EXPORT_REPORT_PLACEHOLDER)}
                        onChange={this.onReportTypeSelect.bind(this)}
                        value={this.state.reportType}/>

                    <p>{textForKey(Locale.TEXT_MODAL_EXPORT_DATERANGE)}</p>
                    <StandardDateRangePicker textForKey={textForKey}
                                             onChange={this.onDateChange.bind(this)}
                                             startDate={startDate}
                                             endDate={endDate}
                                             {...extraOptions}/>
                    {reportSpecificContent}
                </div>
            );
        }

        let downloadButton = null;
        if (showDownload) {
            downloadButton = (
                <Button bsStyle="primary"
                        bsSize="large"
                        onClick={this.onExport.bind(this)}
                        disabled={this.state.downloadDisabled}
                    {...downloadProps}>
                    {downloadText}
                </Button>
            );
        }

        return (
            <Modal bsClass="export-report-holder"
                   show={state.show}
                   onHide={this.onHide.bind(this)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {textForKey(Locale.TEXT_MODAL_TITLE_EXPORT)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {body}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsSize="large" onClick={this.onHide.bind(this)}>
                        {cancelText}
                    </Button>
                    {downloadButton}
                </Modal.Footer>
            </Modal>
        );
    }
}

export interface EditUnitModalProps extends React.Props<EditUnitModalProps> {
    textForKey: Function;
    unitName?: string;
    unitModel?: string;
    unitMake?: string;
    maxValue?: number | string;
    minValue?: number | string;
    unitOfMeasurement?: any;
    limitLines?: boolean;
    state: {
        show: boolean;
        onHide: Function;
    };
    onSubmit: Function;
    variables?: any;
}

export class EditUnitModal extends React.Component<EditUnitModalProps, any> {
    initialState: {
        unitName: string;
        unitModel: string;
        unitMake?: string;
        maxValue: number | string;
        minValue: number | string;
        unitOfMeasurement: string;
        limitLines: boolean;
        saveDisabled: boolean;
        variables?: any;
    };
    state: {
        unitName: string;
        unitModel: string;
        unitMake?: string;
        maxValue: number | string;
        minValue: number | string;
        unitOfMeasurement: string;
        limitLines: boolean;
        saveDisabled: boolean;
        variables?: any;
    };
    constructor(props) {
        super();
        this.initialState = {
            unitName: props.unitName,
            unitModel: props.unitModel,
            unitMake: props.unitMake,
            maxValue: props.maxValue,
            minValue: props.minValue,
            unitOfMeasurement: props.unitOfMeasurement,
            limitLines: props.limitLines,
            variables: props.variables,
            saveDisabled: false
        };
        this.state = this.initialState;
    }
    onHide() {
        this.state = this.initialState;
        this.props.state.onHide();
    }
    onUnitSelect(unit) {
        this.setState({unitOfMeasurement: unit ? unit.value : ""});
    }
    /* *
        Rest of edit Unit controls (they don't exactly work on live, so I had nothing to compare to)
     <label className="info-label">
     Define range of readings for specific unit of measurement.
     <br/>
     Ranges will be visible in chart feature.
     </label>
     <br/>
     <Input type="text" label={textForKey('Range Upper Value')} value={this.state.maxValue}/>
     <Input type="text" label={textForKey('Range Lower Value')} value={this.state.minValue}/>
     <div className="form-group">
     <label>Unit Of Measurement</label>
     <Select value={{value:this.state.unitOfMeasurement, label:this.state.unitOfMeasurement}}
     options={
     [
     {value:"%", label:"%"},
     {value:"°C", label:"°C"}
     ]
     }
     onChange={this.onUnitSelect.bind(this)}
     />
     </div>
    * */
    render() {
        let {state, textForKey} = this.props;
        let cancelText = textForKey(Locale.TEXT_MODAL_BUTTON_CANCEL),
            saveText = textForKey(Locale.TEXT_MODAL_BUTTON_SAVE),
            saveProps: any = {};
        let body = <Tabs defaultActiveKey="info">
                        <Tab eventKey="info" title={textForKey('Unit Info')}>
                            <div className="label-input-holder clearfix">
                                <Input type="text" label={textForKey('Enter Unit Name')} value={this.state.unitName}/>
                                <Input type="text" label={textForKey('Enter Unit Make')} value={this.state.unitMake}/>
                                <Input type="text" label={textForKey('Enter Unit Model')} value={this.state.unitModel}/>
                            </div>
                        </Tab>
                        <Tab eventKey="variables" title={textForKey('Variables')}>
                            <div className="label-input-holder clearfix">
                            </div>
                        </Tab>
                   </Tabs>;
        let saveButton = (
            <Button bsStyle="primary"
                    bsSize="large"
                    onClick={this.props.onSubmit.bind(this)}
                    disabled={this.state.saveDisabled}
                    {...saveProps}>
                {saveText}
            </Button>
        );
        return (
            <Modal bsClass="edit-unit-holder"
                   show={state.show}
                   onHide={this.onHide.bind(this)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {textForKey('Edit Unit')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {body}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsSize="large" onClick={this.onHide.bind(this)}>
                        {cancelText}
                    </Button>
                    {saveButton}
                </Modal.Footer>
            </Modal>
        );
    }

}

interface StandardDateRangePickerProps extends React.Props<StandardDateRangePickerProps> {
    textForKey: Function;
    onChange: (startDate, endDate) => void;
    startDate: any;
    endDate: any;
    dateFormat?: string;
    dateLimit?: any;
    isLive?: boolean;
}

export class StandardDateRangePicker extends React.Component<StandardDateRangePickerProps, any> {

    render() {
        let {textForKey, onChange, startDate, endDate, dateFormat, dateLimit} = this.props;
        let localeFormat = dateFormat || 'DD/MM/YYYY';

        let dateUpdated = (event, picker) => {
            onChange(picker.startDate, picker.endDate);
        };

        let now = moment();
        let weekAgo = moment().subtract(6, 'days');
        let yesterday = moment().subtract(1, 'days');
        let dateRanges = {};
        dateRanges[textForKey(Locale.TEXT_DATE_RANGE_TODAY)] = [now, now];
        dateRanges[textForKey(Locale.TEXT_DATE_RANGE_YESTERDAY)] = [yesterday, yesterday];
        dateRanges[textForKey(Locale.TEXT_DATE_RANGE_LAST7DAYS)] = [weekAgo, now];
        let datePickerOptions = {
            locale: {
                format: localeFormat,
                fromLabel: textForKey(Locale.TEXT_MODAL_EXPORT_FROM),
                toLabel: textForKey(Locale.TEXT_MODAL_EXPORT_TO),
                customRangeLabel: textForKey(Locale.TEXT_MODAL_EXPORT_CUSTOM),
                daysOfWeek: [
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_SU),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_MO),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_TU),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_WE),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_TH),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_FR),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_SA)
                ],
                monthNames: [
                    textForKey(Locale.TEXT_MONTH_JANUARY),
                    textForKey(Locale.TEXT_MONTH_FEBRUARY),
                    textForKey(Locale.TEXT_MONTH_MARCH),
                    textForKey(Locale.TEXT_MONTH_APRIL),
                    textForKey(Locale.TEXT_MONTH_MAY),
                    textForKey(Locale.TEXT_MONTH_JUNE),
                    textForKey(Locale.TEXT_MONTH_JULY),
                    textForKey(Locale.TEXT_MONTH_AUGUST),
                    textForKey(Locale.TEXT_MONTH_SEPTEMBER),
                    textForKey(Locale.TEXT_MONTH_OCTOBER),
                    textForKey(Locale.TEXT_MONTH_NOVEMBER),
                    textForKey(Locale.TEXT_MONTH_DECEMBER)
                ],
                firstDay: 0
            },
            autoApply: true,
            separator: " - ",
            startDate: startDate,
            dateLimit: dateLimit || {days: 6},
            endDate: endDate,
            maxDate: now,
            ranges: dateRanges,
            onEvent: dateUpdated
        };
        let label = `${startDate.format(localeFormat)} - ${endDate.format(localeFormat)}`;

        return (
            <DateRangePicker {...datePickerOptions}>
                <Input type="text" readOnly={true} value={label}/>
            </DateRangePicker>
        );
    }
}

export class DateRangePickerWithLive extends React.Component<StandardDateRangePickerProps, any> {
    render() {
        let {textForKey, onChange, startDate, endDate, dateFormat, isLive} = this.props;
        let localeFormat = dateFormat || 'DD/MM/YYYY';
        let dateUpdated = (event, picker) => {
            if (event.type === "apply") {
                if (picker.chosenLabel && datePickerOptions.ranges[picker.chosenLabel]) {
                    picker.startDate = picker.chosenLabel === "Live Data" ?
                        moment().subtract(4, "hours") :
                        datePickerOptions.ranges[picker.chosenLabel][0];
                    picker.endDate = picker.chosenLabel === "Live Data" ?
                        moment() : datePickerOptions.ranges[picker.chosenLabel][1];
                }
                onChange(picker.startDate, picker.endDate);
            }
        };
        startDate = (typeof startDate ) === 'string' ?
            moment(parseInt(startDate, 10) * 1000) : startDate;
        endDate = (typeof endDate ) === 'string' ?
            moment(parseInt(endDate, 10) * 1000) : endDate;
        let datePickerOptions = {
            opens: "left",
            locale: {
                format: localeFormat,
                fromLabel: textForKey(Locale.TEXT_MODAL_EXPORT_FROM),
                toLabel: textForKey(Locale.TEXT_MODAL_EXPORT_TO),
                customRangeLabel: textForKey(Locale.TEXT_MODAL_EXPORT_CUSTOM),
                daysOfWeek: [
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_SU),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_MO),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_TU),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_WE),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_TH),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_FR),
                    textForKey(Locale.TEXT_DAY_OF_WEEK_ABBREV_SA)
                ],
                monthNames: [
                    textForKey(Locale.TEXT_MONTH_JANUARY),
                    textForKey(Locale.TEXT_MONTH_FEBRUARY),
                    textForKey(Locale.TEXT_MONTH_MARCH),
                    textForKey(Locale.TEXT_MONTH_APRIL),
                    textForKey(Locale.TEXT_MONTH_MAY),
                    textForKey(Locale.TEXT_MONTH_JUNE),
                    textForKey(Locale.TEXT_MONTH_JULY),
                    textForKey(Locale.TEXT_MONTH_AUGUST),
                    textForKey(Locale.TEXT_MONTH_SEPTEMBER),
                    textForKey(Locale.TEXT_MONTH_OCTOBER),
                    textForKey(Locale.TEXT_MONTH_NOVEMBER),
                    textForKey(Locale.TEXT_MONTH_DECEMBER)
                ],
                firstDay: 0
            },
            autoApply: true,
            separator: " - ",
            startDate: startDate,
            endDate: endDate,
            maxDate: moment(),
            dateLimit: {days: 6},
            ranges: {
                [textForKey(TEXT_LIVE_DATA)]:
                 [moment().subtract(4, "hours"), moment()],
                [textForKey(TEXT_TODAY_DATA)]:
                 [moment().startOf('day'), moment().endOf('day')],
                [textForKey(TEXT_YESTERDAY_DATA)]:
                 [(moment().subtract(1, 'days')).startOf('day'),
                  (moment().subtract(1, 'days')).endOf('day')],
                [textForKey(TEXT_7DAY_DATA)]:
                 [(moment().subtract(7, 'days')).startOf('day'),
                  moment().endOf('day')],
            },
            onEvent: dateUpdated
        };
        let label = (_ as any).findKey(datePickerOptions.ranges, ([from, to]) => {
            return startDate.diff(from, 'minutes') === 0 &&
                endDate.diff(to, 'minutes') === 0;
        });
        label = label || `${startDate.format(localeFormat)} - ${endDate.format(localeFormat)}`;

        return (
            <DateRangePicker {...datePickerOptions}>
                <Button>
                    <div>{label}</div>
                    <i className="caret icon-chevron-down icon-white"></i>
                </Button>
            </DateRangePicker>
        );
    }

}

interface SubscribeModalProps extends React.Props<SubscribeModalProps> {
    textForKey: Function;
    updateAction: Function;
    validationErrors: {[input: string]: string[]};
    state: {
        show: boolean;
        onHide: Function;
    };
}


export class SubscribeModal extends React.Component<SubscribeModalProps, any> {
    initialState: {
        mobile: string;
        email: string;
        validForm: any;
        canSubmit: boolean;
        subscriber?: Promise<any>;
    };
    state: {
        url?: string;
        subscriber?: Promise<any>;
        error?: string;
        email?: string;
        mobile?: string;
        validForm: boolean;
        canSubmit: boolean;
    };

    constructor() {
        super();
        this.initialState = {
            mobile: "",
            email: "",
            validForm: false,
            canSubmit: false,
        };
        this.state = this.initialState;
    }

    onHide() {
        let {subscriber} = this.state;
        this.props.state.onHide();
        if (subscriber) {
            subscriber.cancel();
        }
        this.state = this.initialState;
    }

    onSubmit() {
        let {textForKey, updateAction} = this.props;
        let {email, mobile} = this.state;
        let _this = this;

        let exportPromise = updateAction(email, mobile);
        exportPromise.then((response) => {
            if (response.url) {
                Browser.setWindowLocation(response.url);
            }
            _this.setState({
                url: response.url,
                error: response.error,
                exporter: null
            });
        }).catch(() => {
            _this.setState({
                error: textForKey(Locale.TEXT_MODAL_SUBSCRIBE_ERROR),
                exporter: null
            });
        });

        let newState = {
            exporter: exportPromise
        };
        this.setState(newState);
    } // end onSubmit()

    enableButton() {
        this.setState({
            canSubmit: true
        });
    }

    disableButton() {
        this.setState({
            canSubmit: false,
            minRequired: false
        });
    }

    render() {
        let {textForKey, state, updateAction} = this.props;
        // Set form text
        let cancelText = textForKey(Locale.TEXT_MODAL_BUTTON_CANCEL);
        let submitText = textForKey(Locale.TEXT_MODAL_BUTTON_SUBMIT);
        return (
            <Modal bsClass="add-contact-details-holder"
                   show={state.show}
                   onHide={this.onHide.bind(this)}>
                <Formsy.Form className="form-horizontal"
                             onValid={this.enableButton.bind(this)}
                             onInvalid={this.disableButton.bind(this)}
                             onValidSubmit={updateAction}
                             serverValidationErrors={this.props.validationErrors}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {textForKey(Locale.TEXT_MODAL_TITLE_SUBSCRIBE)}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            {textForKey(Locale.TEXT_MODAL_SUBSCRIBE_INSTRUCTION_LINE1)}
                        </p>
                        <p>
                            {textForKey(Locale.TEXT_MODAL_SUBSCRIBE_INSTRUCTION_LINE2)}
                        </p>
                        <SubscribeFormFields textForKey={textForKey}/>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsSize="large" onClick={this.onHide.bind(this)}>
                            {cancelText}
                        </Button>
                        <SubmitButton disabled={!this.state.canSubmit}
                                      defaultValue={submitText}/>
                    </Modal.Footer>
                </Formsy.Form>
            </Modal>
        );
    }
}




